// src/pages/SchoolRules.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import {
  Box, Typography, TextField, Button, IconButton, CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

// Document unique : ce contenu alimente à la fois la page publique du site
// (mkdstudio.co/reglement) et l'écran "Règlement de l'école" dans le profil
// de l'app mobile.
const DOC_REF = ['site_content', 'schoolRules'];

function SchoolRules() {
  const [title, setTitle] = useState("Règlement de l'école");
  const [intro, setIntro] = useState('');
  const [rules, setRules] = useState(['']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const snap = await getDoc(doc(db, ...DOC_REF));
        if (snap.exists()) {
          const data = snap.data();
          setTitle(data.title || "Règlement de l'école");
          setIntro(data.intro || '');
          setRules(data.rules && data.rules.length > 0 ? data.rules : ['']);
        }
      } catch (err) {
        console.error('Erreur chargement règlement:', err);
        setError('Erreur lors du chargement du règlement.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const updateRule = (index, value) => {
    setRules((prev) => prev.map((r, i) => (i === index ? value : r)));
  };

  const addRule = () => setRules((prev) => [...prev, '']);

  const removeRule = (index) => {
    setRules((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const moveRule = (index, direction) => {
    setRules((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const cleanRules = rules.map((r) => r.trim()).filter((r) => r.length > 0);
      if (cleanRules.length === 0) {
        setError('Ajoutez au moins une règle avant d\'enregistrer.');
        setSaving(false);
        return;
      }
      await setDoc(doc(db, ...DOC_REF), {
        title: title.trim() || "Règlement de l'école",
        intro: intro.trim(),
        rules: cleanRules,
        updatedAt: serverTimestamp(),
      });
      setRules(cleanRules);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Erreur enregistrement règlement:", err);
      setError("Erreur lors de l'enregistrement : " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>Règles de l'école</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
        Ce contenu est affiché sur le site public (mkdstudio.co) et dans
        l'app, dans le profil de chaque utilisateur.
      </Typography>

      {error && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fdecea', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: '#c62828' }}>{error}</Typography>
        </Box>
      )}

      <TextField
        label="Titre"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />

      <TextField
        label="Introduction (optionnelle)"
        value={intro}
        onChange={(e) => setIntro(e.target.value)}
        fullWidth
        multiline
        rows={2}
        placeholder="Ex: Merci de prendre connaissance des règles suivantes avant votre inscription."
        sx={{ mb: 3 }}
      />

      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Règles</Typography>

      {rules.map((rule, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
          <Typography sx={{ mt: 1.5, minWidth: 22, color: '#666' }}>{index + 1}.</Typography>
          <TextField
            value={rule}
            onChange={(e) => updateRule(index, e.target.value)}
            fullWidth
            multiline
            placeholder="Ex: Minimum 3 élèves requis pour maintenir un cours."
          />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <IconButton size="small" onClick={() => moveRule(index, -1)} disabled={index === 0}>
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => moveRule(index, 1)} disabled={index === rules.length - 1}>
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </Box>
          <IconButton onClick={() => removeRule(index)} disabled={rules.length === 1} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Button startIcon={<AddIcon />} onClick={addRule} sx={{ mb: 3 }}>
        Ajouter une règle
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
        {saved && <Typography variant="body2" sx={{ color: 'green' }}>Enregistré ✓</Typography>}
      </Box>
    </Box>
  );
}

export default SchoolRules;
